import { Box, Flex } from 'reflexbox';
import { useRouteMatch } from 'react-router';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { FormikProvider, useFormik } from 'formik';
import { useState } from 'react';

import { Redirect, Route, Switch, useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import { SectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import LearningEventsSlider from 'packages/kakapo-ui-application/pages/learning/components/LearningEventsSlider';
import PlainIconNavigationBar from 'packages/kakapo-ui-kit/components/navigation/PlainIconNavigationBar';
import PlainIconNavigationLink from 'packages/kakapo-ui-kit/components/navigation/PlainIconNavigationLink';
import ListViewIcon from 'packages/kakapo-ui-kit/assets/icons/ListViewIcon';
import CalendarIcon from 'packages/kakapo-ui-kit/assets/icons/CalendarIcon';
import LearningFilterSidebar from 'packages/kakapo-ui-application/pages/learning/components/LearningFilterSidebar';
import LearningCalendar from 'packages/kakapo-ui-application/pages/learning/components/calendar/LearningCalendar';

const LearningCalendarRedirect = () => {
	const now = moment();

	return <Redirect to={`/learning/calendar/${now.year()}/${now.month() + 1}`} />;
};

const LearningCalendarDetail = () => {
	const { t } = useTranslation();
	const { params } = useRouteMatch<{ year: string; month: string }>();
	const monthMoment = moment(`${params.year}-${params.month}`, 'YYYY-MM');
	const history = useHistory();

	const formik = useFormik({
		initialValues: {
			types: [],
			subjects: [],
		},
		onSubmit: () => {},
	});

	const [fromDate, setFromDate] = useState<Date>(moment(monthMoment).subtract(4, 'days').toDate());
	const [untilDate, setUntilDate] = useState<Date>(
		moment(monthMoment).add(1, 'month').add(4, 'days').toDate(),
	);

	const { data } = graphql.useLearningEventsQuery({
		variables: {
			first: 100,
			filters: {
				learningTypeIDs: formik.values.types,
				learningSubjectIDs: formik.values.subjects,
				fromDate,
				untilDate,
			},
		},
	});

	const learningEvents = data?.learningEvents.edges
		.map(({ node }) => node)
		.filter((learningEvent) => learningEvent.isViewerAttending || learningEvent.viewerCanJoin);

	return (
		<>
			<Box mb={5}>
				<LearningEventsSlider title={t('learning.events.upcoming.title')} />
			</Box>

			<Flex mb={3} justifyContent="space-between" alignItems="center">
				<SectionTitle>{t('learning.events.explore.title')}</SectionTitle>

				<PlainIconNavigationBar
					renderMenuItems={() => (
						<>
							<PlainIconNavigationLink
								icon={<ListViewIcon />}
								link={{ to: '/learning', title: t('learning.events.navigation.listView') }}
							/>
							<PlainIconNavigationLink
								icon={<CalendarIcon />}
								link={{
									to: '/learning/calendar',
									title: t('learning.events.navigation.calendarView'),
									isActive: true,
								}}
							/>
						</>
					)}
				/>
			</Flex>
			<HorizontalRule />

			<Flex mt={4} flexWrap="wrap">
				<Box width={[1, 1, 1 / 3, 1 / 4]} pr={[0, 0, 3, 4]}>
					<FormikProvider value={formik}>
						<LearningFilterSidebar />
					</FormikProvider>
				</Box>

				<Box width={[1, 1, 2 / 3, 3 / 4]}>
					<LearningCalendar
						events={(learningEvents as graphql.LearningEvent[]) ?? []}
						year={parseInt(params.year, 10)}
						month={parseInt(params.month, 10)}
						onNext={() => {
							const nextMonth = monthMoment.clone().add(1, 'months');
							setFromDate(moment(fromDate).add(1, 'month').toDate());
							setUntilDate(moment(untilDate).add(1, 'month').toDate());

							history.push(`/learning/calendar/${nextMonth.year()}/${nextMonth.month() + 1}`);
						}}
						onPrev={() => {
							const prevMonth = monthMoment.clone().subtract(1, 'months');
							setFromDate(moment(fromDate).subtract(1, 'month').toDate());
							setUntilDate(moment(untilDate).subtract(1, 'month').toDate());

							history.push(`/learning/calendar/${prevMonth.year()}/${prevMonth.month() + 1}`);
						}}
					/>
				</Box>
			</Flex>
		</>
	);
};

const LearningCalendarContainer = () => {
	return (
		<Switch>
			<Route path="/learning/calendar" exact>
				<LearningCalendarRedirect />
			</Route>

			<Route path="/learning/calendar/:year/:month">
				<LearningCalendarDetail />
			</Route>
		</Switch>
	);
};

export default LearningCalendarContainer;
